<template lang="pug">
    div.waf-redirect-url-builder-container
        //- 外層列表
        Row.waf-redirect-url-row(v-if="hasValue" align="middle")
            Col(span="20")
                span {{ getRedirectUrl(value) }}
            Col(span="2")
                Icon(type="md-create" @click="openModal()")
            Col(span="2")
                Icon(type="md-trash" @click="deleteRedirectUrl()")
        Button(v-else @click="addRedirectUrl()") {{ $t("rcmtable.addTagText") }}

        //- Modal
        Modal(v-model="showModal" :title="modalTitle" :mask-closable="false" :closable="false")
            h4.waf-redirect-url-modal-title {{ $t("wafrules.segmentBuilder") }}
            Form(:label-width="80" label-position="left" ref="form" :model="localPort")
                template(v-for="field in redirectUrlFields")
                    //- scheme (下拉選單)
                    FormItem(v-if="field.id === 'scheme'" :label="field.label" :key="field.id")
                        FormSelect(
                            v-if="localValue.scheme && localValue.scheme[0]"
                            v-model="localValue.scheme[0].value"
                            :formItem="field.formItem"
                        )
                    //- port (下拉選單 + 輸入框)
                    FormItem(
                        v-else-if="field.id === 'port'"
                        :label="field.label"
                        :key="field.id"
                        :rules="localPort.type === 'literal' ? field.formInputFormItem.validate : []"
                        prop="value"
                        )
                        FormSelect(v-if="localPort" v-model="localPort.type" :formItem="field.formSelectFormItem")
                        FormInput(
                            v-if="localPort && localPort.type === 'literal'"
                            v-model="localPort.value"
                            :formItem="field.formInputFormItem"
                        )
                    //- host、path、args (Builder)
                    FormItem(v-else :label="field.label" :key="field.id")
                        Builder(
                            :field="field.id"
                            :title-prefix="mode === 'multiple' ? `${getRowTitle(field.id)} ` : ''"
                            :active-insert-index="insertIndex"
                            :active-insert-field="insertField"
                            @on-prepare="prepareInsert"
                            @on-insert="onInsert"
                            :segments="localValue[field.id]"
                            @on-remove="removeSegment"
                            :show-preview-section="showBuilderPreviewSection"
                        )
            h4.waf-redirect-url-modal-title {{ $t("wafrules.preview") }}
            p.waf-redirect-url-modal-preview {{ getRedirectUrl(localValue) }}
            //- Modal 底部按鈕
            div(slot="footer")
                Button(@click="closeModal") {{ $t('rcmtable.cancelText') }}
                Button(@click="confirmSaveRedirectUrl") {{ $t('rcmtable.confirmText') }}
</template>

<script>
import FormSelect from "@/components/rcmTableFormItems/Select/index.js";
import FormInput from "@/components/rcmTableFormItems/Input";
import Builder from "@/components/rcmTableFormItems/SegmentBuilder/Builder.vue";

export default {
    name: "WafRedirectUrlBuilder",
    components: { FormSelect, FormInput, Builder },
    props: {
        value: { type: Object, default: () => ({}) },
        mode: { type: String, default: "single" },
        modalTitle: { type: String, default: "" },
    },
    data() {
        return {
            localValue: JSON.parse(JSON.stringify(this.value)),
            showModal: false,
            redirectUrlFields: [
                {
                    id: "scheme",
                    label: this.$t("wafrules.scheme"),
                    formItem: {
                        options: [
                            { label: "HTTP", value: "http" },
                            { label: "HTTPS", value: "https" },
                        ],
                    },
                },
                { id: "host", label: this.$t("wafrules.host") },
                {
                    id: "port",
                    label: this.$t("wafrules.port"),
                    formSelectFormItem: {
                        options: [
                            { label: this.$t("home.custom"), value: "literal" },
                            { label: this.$t("wafrules.variableDstPort"), value: "system" },
                        ],
                    },
                    formInputFormItem: {
                        validate: [
                            {
                                validator: (rule, value, callback, source, options) => {
                                    const port = Number(value);
                                    const isValid = !isNaN(port) && port >= 1 && port <= 65535;
                                    if (!isValid) {
                                        return callback(new Error(`${this.$t("rcmtable.invalid")}${value}`));
                                    }
                                    callback();
                                },
                                trigger: "blur",
                            },
                        ],
                    },
                },
                { id: "path", label: this.$t("wafrules.path") },
                { id: "args", label: this.$t("wafrules.args") },
            ],
            insertIndex: null,
            insertField: "",
            showBuilderPreviewSection: false,
        };
    },
    computed: {
        hasValue() {
            return Object.keys(this.value).length > 0;
        },
        localPort() {
            return this.localValue.port && Array.isArray(this.localValue.port) && this.localValue.port[0]
                ? this.localValue.port[0]
                : { type: "literal", value: "80" };
        },
    },
    watch: {
        value(val) {
            this.localValue = JSON.parse(JSON.stringify(val));
        },
        "localPort.type": function (newType) {
            if (!this.localPort || !this.localValue.port || !this.localValue.port[0]) return;
            if (newType === "system") {
                this.localPort.value = "dstport";
            } else {
                this.localPort.value = "80";
            }
        },
    },
    methods: {
        getRedirectUrl(data) {
            if (!data || !data.scheme || !data.scheme[0]) return "";

            const { scheme, host, port, path, args } = data;
            const formatSegment = (segment) => (segment.type === "system" ? `{${segment.value}}` : segment.value);
            const joinSegments = (arr) => arr.map(formatSegment).join("");

            let url = "";
            // scheme
            const schemeString = `${scheme[0].value}://`;
            url += schemeString;

            // host
            const hostString = joinSegments(host);
            url += hostString;

            // port
            const targetPort = this.showModal ? this.localPort : port[0];
            const portString = targetPort.type === "system" ? `:{${targetPort.value}}` : `:${targetPort.value}`;
            url += portString;

            // path
            const pathString = joinSegments(path);
            if (pathString) {
                url += pathString.startsWith("/") ? pathString : `/${pathString}`;
            }

            // args
            const argsString = joinSegments(args);
            if (argsString) {
                url += argsString.startsWith("?") ? argsString : `?${argsString}`;
            }

            return url;
        },
        openModal() {
            this.showModal = true;
        },
        deleteRedirectUrl() {
            this.localValue = {};
            this.$refs.form.resetFields();
            this.$emit("input", this.localValue);
        },
        addRedirectUrl() {
            this.localValue = {
                scheme: [{ type: "literal", value: "https" }],
                host: [],
                port: [{ type: "literal", value: "80" }],
                path: [],
                args: [],
            };
            this.openModal();
        },
        getRowTitle(field) {
            return field.charAt(0).toUpperCase() + field.slice(1);
        },
        prepareInsert({ insertIndex, targetField }) {
            this.insertIndex = insertIndex;
            this.insertField = targetField;
        },
        resetInsertStatus() {
            this.insertIndex = null;
            this.insertField = "";
        },
        onInsert(payload) {
            const { targetField, ...data } = payload;
            if (!this.localValue[targetField]) {
                this.resetInsertStatus();
                return;
            }
            this.localValue[targetField].splice(this.insertIndex, 0, data);
            this.resetInsertStatus();
        },
        removeSegment({ field, index }) {
            if (!this.localValue[field]) return;
            this.localValue[field].splice(index, 1);
        },
        closeModal() {
            this.showModal = false;
            this.localValue = JSON.parse(JSON.stringify(this.value));
        },
        async confirmSaveRedirectUrl() {
            const isValid = await this.$refs.form.validate().catch(() => false);
            if (!isValid) return this.$Message.error(this.$t("rcmtable.formErrorMsg"));
            if (this.localValue.host.length === 0) {
                return this.$Message.error(this.$t("wafrules.hostIsRequired"));
            }
            this.$emit("input", JSON.parse(JSON.stringify(this.localValue)));
            this.showModal = false;
        },
    },
};
</script>

<style scoped>
.waf-redirect-url-builder-container {
    display: flex;
    flex-direction: column;
}
.waf-redirect-url-row {
    border-radius: 6px;
    border: 1px solid #dcdee2;
    padding: 8px 16px;
    word-break: break-all;
}
.waf-redirect-url-modal-title {
    margin-bottom: 6px;
}
.waf-redirect-url-modal-preview {
    border-radius: 6px;
    padding: 8px 16px;
    background: #efefef;
    word-break: break-all;
}
</style>



// WEBPACK FOOTER //
// src/components/FormItems/WafRedirectUrlBuilder.vue